import { Slot } from '@radix-ui/react-slot';
import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';

import { OnlyStringKeys } from '@eluve/utils';

import { Icon, IconProps, LoadingIcon } from './Icon';
import { textStyles } from './TextStyles';
import { VariantProps, tv } from './cn';

export const newButtonStyles = tv({
  slots: {
    button:
      'relative inline-flex cursor-pointer select-none items-center justify-center',
    content:
      'inline-flex min-w-0 items-center justify-center text-nowrap opacity-100 transition-opacity [&>span]:flex-1 [&>span]:truncate',
    spinner: 'pointer-events-none absolute opacity-0 transition-opacity',
  },
  variants: {
    disabled: { true: { button: 'cursor-not-allowed' } },
    iconOnly: { true: { button: 'aspect-square !px-0' } },
    loading: { true: { content: 'opacity-0', spinner: 'opacity-100' } },
    size: {
      s: {
        button: 'rounded-md px-2',
        content: textStyles.body({
          color: 'inherit',
          size: 'sm',
          className: 'gap-1.5',
        }),
      },
      m: {
        button: 'rounded-lg px-3',
        content: textStyles.body({
          color: 'inherit',
          size: 'md',
          className: 'gap-2',
        }),
      },
      l: {
        button: 'rounded-xl px-4',
        content: textStyles.body({
          color: 'inherit',
          size: 'lg',
          className: 'gap-3',
        }),
      },
    },
    spacing: {
      default: {},
      compact: {},
    },
    type: {
      primary: {},
      subtle: {},
      outline: {},
      outlineSubtle: {},
      outlineFilled: {},
      ghost: {},
    },
    variant: {
      default: {},
      brand: {},
    },
    wFull: {
      true: { button: 'w-full' },
    },
  },
  compoundVariants: [
    // Default Variant
    {
      type: 'primary',
      variant: 'default',
      className: {
        button:
          'bg-bg-strong-primary text-text-strong hover:bg-bg-strong-secondary',
      },
    },
    {
      type: 'subtle',
      variant: 'default',
      className: {
        button:
          'bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary hover:text-text-primary',
      },
    },
    {
      type: 'outline',
      variant: 'default',
      className: {
        button:
          'border border-border-primary bg-white text-text-primary hover:border-bg-tertiary hover:bg-white hover:text-text-secondary',
      },
    },
    {
      type: 'outlineSubtle',
      variant: 'default',
      className: {
        button:
          'border border-border-primary bg-white text-text-tertiary hover:border-border-secondary hover:bg-white hover:text-text-secondary',
      },
    },
    {
      type: 'outlineFilled',
      variant: 'default',
      className: {
        button:
          'border border-border-primary bg-bg-secondary text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary',
      },
    },
    {
      type: 'ghost',
      variant: 'default',
      className: {
        button:
          'text-text-primary hover:bg-bg-tertiary hover:text-text-secondary',
      },
    },
    {
      disabled: true,
      type: 'primary',
      variant: 'default',
      className: {
        button:
          'bg-bg-secondary text-text-supporting hover:bg-bg-secondary hover:text-text-supporting',
      },
    },
    {
      disabled: true,
      type: 'subtle',
      variant: 'default',
      className: {
        button:
          'bg-bg-secondary text-text-supporting hover:bg-bg-secondary hover:text-text-supporting',
      },
    },
    {
      disabled: true,
      type: 'outline',
      variant: 'default',
      className: {
        button:
          'border-bg-tertiary bg-white text-text-disabled hover:border-bg-tertiary hover:bg-white hover:text-text-disabled',
      },
    },
    {
      disabled: true,
      type: 'outlineSubtle',
      variant: 'default',
      className: {
        button:
          'border-bg-tertiary bg-white text-text-disabled hover:border-bg-tertiary hover:bg-white hover:text-text-disabled',
      },
    },
    {
      disabled: true,
      type: 'outlineFilled',
      variant: 'default',
      className: {
        button:
          'border-bg-tertiary bg-bg-secondary text-text-supporting hover:border-bg-tertiary hover:bg-bg-secondary hover:text-text-supporting',
      },
    },
    {
      disabled: true,
      type: 'ghost',
      variant: 'default',
      className: {
        button:
          'text-text-disabled hover:bg-transparent hover:text-text-disabled',
      },
    },
    // Brand Variant
    {
      type: 'primary',
      variant: 'brand',
      className: {
        button:
          'bg-bg-indigo-strong-primary text-text-indigo-strong hover:bg-bg-indigo-strong-secondary',
      },
    },
    {
      type: 'subtle',
      variant: 'brand',
      className: {
        button:
          'bg-bg-indigo-subtle-primary text-text-indigo-subtle hover:bg-bg-indigo-subtle-secondary',
      },
    },
    {
      type: 'outline',
      variant: 'brand',
      className: {
        button:
          'border border-border-indigo-strong bg-white text-text-indigo hover:border-border-indigo-strong hover:bg-bg-indigo-subtle-primary hover:text-text-indigo',
      },
    },
    {
      type: 'outlineSubtle',
      variant: 'brand',
      className: {
        button:
          'border border-border-indigo-subtle bg-white text-text-indigo hover:border-border-indigo-subtle hover:bg-bg-indigo-subtle-primary hover:text-text-indigo',
      },
    },
    {
      type: 'outlineFilled',
      variant: 'brand',
      className: {
        button:
          'border border-border-indigo-subtle bg-bg-indigo-subtle-primary text-text-indigo hover:border-border-indigo-subtle hover:bg-bg-indigo-subtle-secondary hover:text-text-indigo',
      },
    },
    {
      type: 'ghost',
      variant: 'brand',
      className: {
        button:
          'text-text-indigo hover:bg-bg-indigo-subtle-primary hover:text-text-indigo',
      },
    },
    {
      disabled: true,
      type: 'primary',
      variant: 'brand',
      className: {
        button:
          'bg-bg-indigo-subtle-primary text-text-indigo/40 hover:bg-bg-indigo-subtle-primary',
      },
    },
    {
      disabled: true,
      type: 'subtle',
      variant: 'brand',
      className: {
        button:
          'bg-bg-indigo-subtle-primary text-text-indigo/40 hover:bg-bg-indigo-subtle-primary',
      },
    },
    {
      disabled: true,
      type: 'outline',
      variant: 'brand',
      className: {
        button:
          'border-border-indigo-strong/40 bg-white text-text-indigo/40 hover:border-border-indigo-strong/40 hover:bg-white hover:text-text-indigo/40',
      },
    },
    {
      disabled: true,
      type: 'outlineSubtle',
      variant: 'brand',
      className: {
        button:
          'border-border-indigo-subtle/40 bg-white text-text-indigo/40 hover:border-border-indigo-subtle/40 hover:bg-white hover:text-text-indigo/40',
      },
    },
    {
      disabled: true,
      type: 'outlineFilled',
      variant: 'brand',
      className: {
        button:
          'border-border-indigo-subtle/40 bg-bg-indigo-subtle-primary text-text-indigo/40 hover:border-border-indigo-subtle/40 hover:bg-bg-indigo-subtle-primary hover:text-text-indigo/40',
      },
    },
    {
      disabled: true,
      type: 'ghost',
      variant: 'brand',
      className: {
        button:
          'text-text-indigo/40 hover:bg-transparent hover:text-text-indigo/40',
      },
    },
    // Spacing + Size
    {
      spacing: 'default',
      size: 's',
      className: { button: 'h-[30px] py-1.5' },
    },
    {
      spacing: 'default',
      size: 'm',
      className: { button: 'h-[40px] py-2' },
    },
    {
      spacing: 'default',
      size: 'l',
      className: { button: 'h-[48px] py-2.5' },
    },
    {
      spacing: 'compact',
      size: 's',
      className: { button: 'h-[26px] py-1' },
    },
    {
      spacing: 'compact',
      size: 'm',
      className: { button: 'h-[32px] py-1.5' },
    },
    {
      spacing: 'compact',
      size: 'l',
      className: { button: 'h-[44px] py-2' },
    },
  ],
  defaultVariants: {
    type: 'primary',
    spacing: 'default',
    size: 'm',
    variant: 'default',
  },
});

type ButtonSize = OnlyStringKeys<CoreButtonStyleProps['size']>;
type IconSize = OnlyStringKeys<IconProps['size']>;

const IconSizeByButtonSize: { [key in ButtonSize]: IconSize } = {
  s: '2xs',
  m: 'xs',
  l: 'sm',
};

// Core

type CoreButtonStyleProps = Omit<
  VariantProps<typeof newButtonStyles>,
  'iconOnly'
>;

type CoreButtonContentProps = {
  icon?: {
    name: IconProps['name'];
    position?: 'left' | 'right';
  };
  loading?: CoreButtonStyleProps['loading'];
  size?: CoreButtonStyleProps['size'];
  text?: string;
};

type CoreButtonProps = CoreButtonContentProps & CoreButtonStyleProps;

type CoreButtonPropsWithChildren = React.PropsWithChildren<CoreButtonProps>;

export const CoreButtonContent: React.FC<CoreButtonContentProps> = ({
  icon,
  loading,
  size = 'm',
  text,
}) => {
  const { content: contentClassName, spinner: spinnerClassname } =
    newButtonStyles({ loading, size });

  return (
    <>
      <span className={contentClassName()}>
        {icon && icon.position !== 'right' && (
          <Icon name={icon.name} size={IconSizeByButtonSize[size]} />
        )}
        {text && <span>{text}</span>}
        {icon && icon.position === 'right' && (
          <Icon name={icon.name} size={IconSizeByButtonSize[size]} />
        )}
      </span>
      {loading !== undefined && (
        <LoadingIcon
          className={spinnerClassname()}
          size={IconSizeByButtonSize[size]}
        />
      )}
    </>
  );
};

const CoreButton = forwardRef<HTMLElement, CoreButtonPropsWithChildren>(
  (
    {
      children,
      disabled,
      icon,
      loading,
      size,
      spacing,
      text,
      type,
      variant,
      wFull,
      ...props
    },
    ref,
  ) => {
    const iconOnly = icon != null && text == null;
    const { button: buttonClassName } = newButtonStyles({
      disabled: disabled || loading,
      iconOnly,
      size,
      spacing,
      type,
      variant,
      wFull,
    });
    return (
      <Slot
        children={children}
        className={buttonClassName()}
        ref={ref}
        {...props}
      />
    );
  },
);

// Button

export type NewButtonProps = CoreButtonProps & {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  submit?: boolean;
};

export const NewButton = forwardRef<HTMLButtonElement, NewButtonProps>(
  ({ onClick, submit, ...props }, ref) => {
    const { disabled, icon, loading, size, text } = props;
    return (
      <CoreButton ref={ref} {...props}>
        <button
          disabled={disabled || loading}
          onClick={disabled || loading ? undefined : onClick}
          type={submit ? 'submit' : 'button'}
        >
          <CoreButtonContent
            icon={icon}
            loading={loading}
            size={size}
            text={text}
          />
        </button>
      </CoreButton>
    );
  },
);

// Link

type AnchorLinkButtonProps = {
  href: string;
  rel?: string;
  target?: string;
  to?: never;
};

type RouterLinkButtonProps = {
  href?: never;
  rel?: never;
  target?: never;
  to: string;
};

export type LinkButtonProps = CoreButtonProps &
  (AnchorLinkButtonProps | RouterLinkButtonProps);

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ href, rel = 'noreferrer', target = '_blank', to, ...props }, ref) => {
    const { disabled, icon, loading, size, text } = props;

    if (disabled || loading) {
      return (
        <CoreButton ref={ref} {...props}>
          <button disabled type="button">
            <CoreButtonContent
              icon={icon}
              loading={loading}
              size={size}
              text={text}
            />
          </button>
        </CoreButton>
      );
    }

    return (
      <CoreButton ref={ref} {...props}>
        {to != null ? (
          <Link to={to}>
            <CoreButtonContent
              icon={icon}
              loading={loading}
              size={size}
              text={text}
            />
          </Link>
        ) : (
          <a href={href} rel={rel} target={target}>
            <CoreButtonContent
              icon={icon}
              loading={loading}
              size={size}
              text={text}
            />
          </a>
        )}
      </CoreButton>
    );
  },
);
